"use client";

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

import { CloseIcon } from "@/components/appIcons";
import { formatDuration } from "@/lib/format-duration";
import { invalidateFeedViews } from "@/lib/social-query-invalidation";
import { client, orpc } from "@/utils/orpc.client";

type ScrobbleEntity = "work" | "release";

interface LastfmScrobbleDialogProps {
  entity: ScrobbleEntity;
  id: string;
  open: boolean;
  onClose: () => void;
  onReconnectRequired?: () => void;
}

const FALLBACK_DURATION_SECONDS = 180;

function toLocalInputValue(date: Date): string {
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function selectedDurationSeconds(
  tracks: Array<{ id: string; durationSeconds: number | null; scrobbleable: boolean }>,
  selectedIds: Set<string>,
): number {
  return tracks.reduce((sum, track) => {
    if (!track.scrobbleable || !selectedIds.has(track.id)) return sum;
    return sum + (track.durationSeconds ?? FALLBACK_DURATION_SECONDS);
  }, 0);
}

function invalidateFeedViewsAfterScrobble(queryClient: QueryClient) {
  void invalidateFeedViews(queryClient);
  window.setTimeout(() => {
    void invalidateFeedViews(queryClient);
  }, 2500);
}

function scrobbleSubmitDisabled(input: {
  contextAvailable: boolean;
  pending: boolean;
  selectedCount: number;
  startedAt: string;
}): boolean {
  if (!input.contextAvailable) return true;
  if (input.selectedCount === 0) return true;
  if (input.startedAt.length === 0) return true;
  return input.pending;
}

function scrobbleInput(entity: ScrobbleEntity, id: string) {
  if (entity === "work") return { entity, id } as const;
  return { entity, id } as const;
}

export function LastfmScrobbleDialog({
  entity,
  id,
  open,
  onClose,
  onReconnectRequired,
}: LastfmScrobbleDialogProps) {
  const t = useTranslations("shared.lastfmScrobble");
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [startedAt, setStartedAt] = useState("");
  const [initializedFor, setInitializedFor] = useState<string | null>(null);
  const input = scrobbleInput(entity, id);

  const contextQuery = useQuery({
    ...orpc.lastfm.getScrobbleContext.queryOptions({ input }),
    enabled: open,
  });

  const context = contextQuery.data;
  const selectableTracks = useMemo(
    () => context?.tracks.filter((track) => track.scrobbleable) ?? [],
    [context],
  );

  useEffect(() => {
    if (!open) {
      setInitializedFor(null);
      return;
    }
    if (!context) return;

    const key = `${entity}:${id}:${context.tracks.length}`;
    if (initializedFor === key) return;

    const initialSelectedIds = new Set(selectableTracks.map((track) => track.id));
    setSelectedIds(initialSelectedIds);
    const durationMs = selectedDurationSeconds(context.tracks, initialSelectedIds) * 1000;
    setStartedAt(toLocalInputValue(new Date(Date.now() - durationMs)));
    setInitializedFor(key);
  }, [context, entity, id, initializedFor, open, selectableTracks]);

  const selectedCount = selectedIds.size;
  const selectedDuration = context ? selectedDurationSeconds(context.tracks, selectedIds) : 0;

  const scrobbleMutation = useMutation({
    mutationFn: async () =>
      client.lastfm.scrobble({
        ...input,
        id,
        startedAt: new Date(startedAt).toISOString(),
        trackIds: [...selectedIds],
      }),
    onSuccess: (result) => {
      toast.success(t("success", { count: result.accepted }));
      invalidateFeedViewsAfterScrobble(queryClient);
      onClose();
    },
    onError: (error: Error & { data?: { code?: string } }) => {
      if (error.data?.code === "LASTFM_RECONNECT_REQUIRED") {
        onReconnectRequired?.();
      }
      toast.error(error.message);
    },
  });
  const submitDisabled = scrobbleSubmitDisabled({
    contextAvailable: Boolean(context),
    pending: scrobbleMutation.isPending,
    selectedCount,
    startedAt,
  });

  if (!open) return null;

  const toggleTrack = (trackId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) {
        next.delete(trackId);
      } else {
        next.add(trackId);
      }
      return next;
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 flex md:items-center md:justify-center"
      role="dialog"
      aria-modal="true"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="bg-card md:rounded-2xl md:max-w-2xl w-full md:mx-4 h-dvh md:h-auto max-h-dvh md:max-h-[85vh] flex flex-col md:shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="text-lg font-bold">{t("title")}</h2>
            {context && (
              <p className="text-sm text-muted-foreground truncate" title={context.albumTitle}>
                {context.albumTitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="relative after:absolute after:-inset-1.5 after:content-[''] size-8 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
            aria-label={t("close")}
          >
            <CloseIcon className="text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {contextQuery.isLoading && (
            <div className="h-28 rounded-lg bg-secondary/70 animate-pulse" aria-hidden />
          )}

          {!contextQuery.isLoading && !context && (
            <p className="text-sm text-muted-foreground">{t("empty")}</p>
          )}

          {context && (
            <>
              <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
                <label className="block">
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    {t("startedAt")}
                  </span>
                  <input
                    type="datetime-local"
                    value={startedAt}
                    onChange={(event) => setStartedAt(event.target.value)}
                    className="mt-1 w-full max-sm:min-h-11 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  />
                </label>
                <div className="rounded-lg bg-secondary/40 px-3 py-2 text-sm">
                  <span className="text-muted-foreground">{t("selected")}</span>{" "}
                  <span className="font-bold">
                    {selectedCount} / {selectableTracks.length}
                  </span>
                  <span className="mx-2 text-muted-foreground">·</span>
                  <span className="font-bold">{formatDuration(selectedDuration)}</span>
                </div>
              </div>

              <div className="space-y-1">
                {context.tracks.map((track) => {
                  const checked = selectedIds.has(track.id);
                  const disabled = !track.scrobbleable;
                  return (
                    <label
                      key={`${track.position}-${track.id}`}
                      className={`grid grid-cols-[auto_2.5rem_1fr_auto] items-center gap-3 rounded-lg px-3 py-2 transition-colors ${
                        disabled ? "opacity-45" : "hover:bg-secondary cursor-pointer"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleTrack(track.id)}
                        className="size-4 accent-primary"
                      />
                      <span className="text-sm text-muted-foreground">{track.number}</span>
                      <span className="min-w-0">
                        <span className="block truncate font-medium">
                          {track.title || t("untitled")}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {track.artistName || t("missingArtist")}
                        </span>
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {track.durationSeconds != null
                          ? formatDuration(track.durationSeconds)
                          : t("unknownDuration")}
                      </span>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="p-6 pt-4 border-t border-border flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-lg border border-border hover:bg-secondary"
          >
            {t("cancel")}
          </button>
          <button
            onClick={() => scrobbleMutation.mutate()}
            disabled={submitDisabled}
            className="flex-1 py-2.5 text-sm font-bold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {scrobbleMutation.isPending ? t("submitting") : t("submit")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
